import { workOrdersApi } from 'api/easypay'
import { IS_ACCEPTOR_SELECTION_ENABLED } from 'commons/constants'
import CommonButton from 'components/CommonButton'
import HeadingParagraph from 'components/HeadingParagraph'
import { useQuery } from 'Hooks/useQuery'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { populateWorkOrder } from 'store/slices/appMiscSlice'
import { setStepAmount } from 'store/slices/registrationStepper'
import { logOutRequest } from 'store/slices/userSlice'
import { LocalStorageService } from 'utils/LocalStorageService'
import numbersUtil from 'utils/numberFormatUtils'
import { printToast } from 'utils/printToast'

const RecapPage: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const query = useQuery()

  const [refreshPage, setRefreshPage] = useState(false)
  const [workOrder, setWorkOrder] = useState<any>(
    LocalStorageService.getObject()?.workOrder
  )

  const [selectedAcceptor, setSelectedAcceptor] = useState<any>(null)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  
  // Stato per gestire l'apertura della nuova select custom
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)

  useEffect(() => {
    if (window.location.href.includes('?workOrderId=')) {
      findWorkOrderById(
        decodeURIComponent(window.location.href.split('?workOrderId=')[1]),
      )
    }
  }, [])

  useEffect(() => {
    if (!workOrder) return

    if (workOrder.status === "EXPIRED") {
      printToast.error('Il link è scaduto', 5000)
    } else if (workOrder.status === "PAID") {
      printToast.error("L'ordine di lavoro è già stato pagato", 5000)
    } else if (workOrder.status === "IN_PROGRESS") {
      printToast.error("L'ordine di lavoro è in corso di pagamento", 5000)
    }

    let workOrderCheck = workOrder?.uploadedByDealer ? workOrder.uploadedByDealer : false
    dispatch(setStepAmount(workOrderCheck === true ? 4 : 6))
  }, [workOrder, dispatch])

  async function findWorkOrderById(workOrderId) {
    workOrdersApi
      .getWorkOrderById({
        workOrderId: workOrderId,
      })
      .then(response => {
        const wo = response.data[0]

        dispatch(
          populateWorkOrder({
            workOrder: wo,
            status: 'uncompleted',
          }),
        )

        LocalStorageService.addObject({ workOrder: wo })
        setWorkOrder(wo)
        setRefreshPage(prev => !prev)
      })
      .catch(error => {
        console.error(error)
      })
  }

  const goBack = () => {
    dispatch(logOutRequest({ targetPath: '/', navigate }))
  }

  if (!workOrder || !Object.entries(workOrder).length) {
    goBack()
    return null
  }

  const acceptors = workOrder?.availableAcceptors || []

  return (
    <>
      {workOrder.status === 'PENDING' && (
        <div className="mb-2 text-left w-full px-5 pb-5">
          <h1 className="small-warning text-gray-700 mb-2">
            Si prega di verificare la correttezza dei dati. In caso di errori
            rieseguire l&apos;acquisizione del documento
          </h1>

          <br />

          <HeadingParagraph headingLevel={2} title="Intestatario" name={workOrder.name} />
          <HeadingParagraph headingLevel={3} title="Concessionaria" name={workOrder.dealerName} />
          <HeadingParagraph headingLevel={4} title="Targa" name={workOrder.plate} />

          <HeadingParagraph
            headingLevel={5}
            title={'workOrder' in workOrder ? 'Ordine' : 'Fattura'}
            name={`${'invoice' in workOrder && 'workOrder' in workOrder
              ? `${workOrder.workOrder}`
              : 'invoice' in workOrder
                ? workOrder.invoice
                : workOrder?.workOrder
              } - ${moment(workOrder.date).format('DD-MM-YYYY')}`}
          />

          <HeadingParagraph
            headingLevel={6}
            cssProps="pb-4 font-weight-bold text-third"
            title="Prezzo"
            name={numbersUtil.format(workOrder.totalAmount) + ' €'}
          />

          <HeadingParagraph headingLevel={6} title="Modello" name={workOrder.carModel} />

          {IS_ACCEPTOR_SELECTION_ENABLED && acceptors.length > 0 && (
            <div className="mt-6 text-left relative">
              <label className="block mb-1.5 text-sm font-medium text-gray-700">
                Seleziona accettatore
              </label>

              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`w-full flex justify-between items-center bg-white border rounded-lg px-4 py-2.5 text-left shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  isDropdownOpen ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <span className={!selectedAcceptor ? 'text-gray-400' : 'text-gray-900 font-medium'}>
                  {selectedAcceptor
                    ? selectedAcceptor.email === 'NOT_SPECIFIED'
                      ? "Non ricordo l'accettatore"
                      : `${selectedAcceptor.name} ${selectedAcceptor.surname}`
                    : '-- Seleziona --'}
                </span>
                
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setIsDropdownOpen(false)} />

                  <ul className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-auto py-1 focus:outline-none">
                    
                    <li
                      className="cursor-pointer select-none py-2.5 px-4 text-gray-400 hover:bg-gray-50 transition-colors text-sm"
                      onClick={() => {
                        setSelectedAcceptor(null)
                        setIsDropdownOpen(false)
                      }}
                    >
                      -- Seleziona --
                    </li>

                    <li
                      className={`cursor-pointer select-none py-2.5 px-4 border-b border-gray-100 transition-colors text-sm font-medium ${
                        selectedAcceptor?.email === 'NOT_SPECIFIED'
                          ? 'bg-blue-50 text-blue-600'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        setSelectedAcceptor({
                          name: "Non ricordato",
                          surname: "",
                          email: "NOT_SPECIFIED",
                          role: "ACCEPTOR"
                        })
                        setIsDropdownOpen(false)
                      }}
                    >
                      Non ricordo l&apos;accettatore
                    </li>

                    {acceptors.map((acc: any, index: number) => {
                      const isSelected = selectedAcceptor?.email === acc.email
                      return (
                        <li
                          key={index}
                          className={`cursor-pointer select-none py-2.5 px-4 text-sm transition-colors ${
                            isSelected
                              ? 'bg-blue-50 text-blue-600 font-semibold'
                              : 'text-gray-800 hover:bg-gray-50'
                          }`}
                          onClick={() => {
                            setSelectedAcceptor(acc)
                            setIsDropdownOpen(false)
                          }}
                        >
                          {acc.name} {acc.surname}
                        </li>
                      )
                    })}
                  </ul>
                </>
              )}

              {!selectedAcceptor && (
                <p className="text-red-500 mt-2 text-xs flex items-center gap-1.5 animate-pulse">
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                  </svg>
                  Devi selezionare un accettatore per continuare
                </p>
              )}
            </div>
          )}

          <div className="mt-8 text-center">
            <CommonButton
              type="button"
              name={isSubmitting ? "Invio in corso..." : "Vai al pagamento"}
              onClick={async () => {
                if (!IS_ACCEPTOR_SELECTION_ENABLED) {
                  navigate('/choose-option')
                  return
                }

                if (!selectedAcceptor) {
                  printToast.error('Seleziona un accettatore')
                  return
                }

                setIsSubmitting(true)

                try {
                  await workOrdersApi.setSelectedAcceptor({
                    acceptorEmail: selectedAcceptor.email,
                    workOrderId: `${workOrder.code}/${workOrder.dealerCode}`
                  });

                  const updatedWorkOrder = {
                    ...workOrder,
                    selectedAcceptor
                  }

                  LocalStorageService.addObject({ workOrder: updatedWorkOrder })
                  navigate('/choose-option')
                } catch (error) {
                  console.error("Errore durante l'assegnazione dell'accettatore:", error)
                  printToast.error("Impossibile salvare l'accettatore selezionato. Riprova.")
                } finally {
                  setIsSubmitting(false)
                }
              }}
              disabled={(IS_ACCEPTOR_SELECTION_ENABLED && !selectedAcceptor) || isSubmitting}
            />
          </div>

          <div className="mt-5 text-center">
            <CommonButton
              type="button"
              name="Indietro"
              onClick={goBack}
              cssType="fill-in-white"
              disabled={isSubmitting}
            />
          </div>
        </div>
      )}
    </>
  )
}

export default RecapPage